package dev.lingvo.site.api

import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.CaptureSource
import dev.lingvo.shared.CoverageDto
import dev.lingvo.shared.CreateSlotRequest
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.InvalidateCapturesRequest
import dev.lingvo.shared.InvalidateResultDto
import dev.lingvo.shared.PutScreenshotPlanRequest
import dev.lingvo.shared.ReorderSlotsRequest
import dev.lingvo.shared.RetakeQueueDto
import dev.lingvo.shared.ReviewCaptureRequest
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.ScreenshotQuotaDto
import dev.lingvo.shared.ScreenshotSlotDto
import dev.lingvo.shared.ShotPlanChangeDto
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.UpdateSlotRequest
import dev.lingvo.shared.UploadTicketDto
import dev.lingvo.shared.UploadTicketRequest
import kotlinx.serialization.decodeFromString
import org.w3c.files.Blob

/** Reads the plan without creating screenshot state. */
suspend fun ApiClient.screenshotPlan(slug: String, platform: StorePlatform): ScreenshotPlanDto =
    jsonRequest("GET", captureBase(slug, platform) + "/plan")

/** Protects a plan save with its reviewed version and supports a preview of destructive changes. */
suspend fun ApiClient.putScreenshotPlan(
    slug: String, platform: StorePlatform, request: PutScreenshotPlanRequest, dryRun: Boolean = false, ifMatch: String? = null,
): ShotPlanChangeDto =
    jsonRequest("PUT", captureBase(slug, platform) + "/plan" + query("dryRun" to dryRun.takeIf { it }?.toString()), request,
        headers = ifMatch?.let { mapOf("If-Match" to it) }.orEmpty())

/** Creates a plan slot with its caption key. */
suspend fun ApiClient.createScreenshotSlot(slug: String, platform: StorePlatform, request: CreateSlotRequest): ScreenshotSlotDto =
    jsonRequest("POST", captureBase(slug, platform) + "/slots", request)

/** Changes one stable slot without identifying it by position. */
suspend fun ApiClient.updateScreenshotSlot(slug: String, platform: StorePlatform, slotId: String, request: UpdateSlotRequest): ScreenshotSlotDto =
    jsonRequest("PATCH", captureBase(slug, platform) + "/slots/${segment(slotId)}", request)

/** Deletes only the explicitly selected slot. */
suspend fun ApiClient.deleteScreenshotSlot(slug: String, platform: StorePlatform, slotId: String): Unit =
    unitRequest("DELETE", captureBase(slug, platform) + "/slots/${segment(slotId)}")

/** Reorders against the caller's plan version. */
suspend fun ApiClient.reorderScreenshotSlots(slug: String, platform: StorePlatform, request: ReorderSlotsRequest): ScreenshotPlanDto =
    jsonRequest("POST", captureBase(slug, platform) + "/slots/reorder", request)

/** Queues retakes for the selected slot scope. */
suspend fun ApiClient.invalidateScreenshotSlot(slug: String, platform: StorePlatform, slotId: String, request: InvalidateCapturesRequest): InvalidateResultDto =
    jsonRequest("POST", captureBase(slug, platform) + "/slots/${segment(slotId)}/invalidate", request)

/** Reads retakes with encoded locale and canonical device filters. */
suspend fun ApiClient.screenshotRetakes(
    slug: String, platform: StorePlatform, locale: String? = null, device: DeviceClass? = null,
): RetakeQueueDto =
    jsonRequest("GET", captureBase(slug, platform) + "/retakes" + query("locale" to locale, "device" to device?.id))

/** Lists metadata separately from authenticated image bytes. */
suspend fun ApiClient.screenshotCaptures(
    slug: String, platform: StorePlatform, locale: String? = null, slot: String? = null, superseded: Boolean = false,
): List<CaptureDto> =
    jsonRequest("GET", captureBase(slug, platform) + "/captures" + query("locale" to locale, "slot" to slot,
        "superseded" to superseded.takeIf { it }?.toString()))

/** Fetches image bytes with the current bearer rather than an unauthenticated img request. */
suspend fun ApiClient.screenshotCaptureImage(slug: String, platform: StorePlatform, captureId: String): Blob =
    blobRequest("GET", captureBase(slug, platform) + "/captures/${segment(captureId)}/image", "image/png, image/jpeg")

/** Deletes the selected capture through the server's approval checks. */
suspend fun ApiClient.deleteScreenshotCapture(slug: String, platform: StorePlatform, captureId: String): Unit =
    unitRequest("DELETE", captureBase(slug, platform) + "/captures/${segment(captureId)}")

/** Restores a retained capture through the normal locked persistence path. */
suspend fun ApiClient.restoreScreenshotCapture(slug: String, platform: StorePlatform, captureId: String): CaptureDto =
    jsonRequest("POST", captureBase(slug, platform) + "/captures/${segment(captureId)}/restore")

/** Records a human capture review with the session bearer. */
suspend fun ApiClient.reviewScreenshotCapture(slug: String, platform: StorePlatform, captureId: String, request: ReviewCaptureRequest): CaptureDto =
    jsonRequest("POST", captureBase(slug, platform) + "/captures/${segment(captureId)}/review", request)

/** Reads authoritative byte quotas before presenting upload choices. */
suspend fun ApiClient.screenshotUsage(slug: String, platform: StorePlatform): ScreenshotQuotaDto =
    jsonRequest("GET", captureBase(slug, platform) + "/usage")

/** Builds an encoded raw-upload path shared by Blob and ArrayBuffer uploaders. */
fun ApiClient.screenshotCaptureUploadPath(
    slug: String, platform: StorePlatform, slotId: String, device: DeviceClass, locale: String,
    source: CaptureSource = CaptureSource.WEB, basis: String? = null,
): String =
    captureBase(slug, platform) + "/slots/${segment(slotId)}/captures/${segment(device.id)}/${segment(locale)}" +
        query("source" to source.name, "basis" to basis)

/** Requests a bounded one-use upload ticket for an external capture process. */
suspend fun ApiClient.screenshotUploadTicket(
    slug: String, platform: StorePlatform, slotId: String, device: DeviceClass, locale: String, request: UploadTicketRequest,
): UploadTicketDto =
    jsonRequest("POST", captureBase(slug, platform) + "/slots/${segment(slotId)}/captures/${segment(device.id)}/${segment(locale)}/ticket", request)

/** Null coverage means HTTP 304; the paired ETag is retained for the next polling request. */
suspend fun ApiClient.screenshotCoverage(
    slug: String, platform: StorePlatform, ifNoneMatch: String? = null,
): Pair<CoverageDto?, String?> {
    val path = captureBase(slug, platform) + "/coverage"
    val response = try {
        apiRequest("GET", path, extraHeaders = ifNoneMatch?.let { mapOf("If-None-Match" to it) }.orEmpty())
    } catch (failure: ApiException) {
        if (failure.status == 304 && ifNoneMatch != null) return null to ifNoneMatch
        throw failure
    }
    val text = responseText(response, path)
    val coverage = try { API_JSON.decodeFromString<CoverageDto>(text) } catch (failure: Throwable) {
        throw invalidResponse(path, failure)
    }
    return coverage to response.headers.get("ETag")
}

/** Sends a raw capture with progress and optimistic replacement headers. */
suspend fun ApiClient.putScreenshotCapture(
    slug: String, platform: StorePlatform, slotId: String, device: DeviceClass, locale: String,
    body: Blob, contentType: String, source: CaptureSource = CaptureSource.WEB, basis: String? = null,
    ifMatch: String? = null, ifNoneMatch: String? = null, upload: XhrUpload = XhrUpload(),
    onProgress: (Long, Long) -> Unit = { _, _ -> },
): CaptureDto = upload.put(screenshotCaptureUploadPath(slug, platform, slotId, device, locale, source, basis),
    body, contentType, ifMatch, ifNoneMatch, onProgress) { API_JSON.decodeFromString<CaptureDto>(it) }

/** Redeems an external uploader's single-use capability without sending a person's bearer. */
suspend fun ApiClient.redeemScreenshotUploadTicket(
    ticket: String, body: Blob, contentType: String, upload: XhrUpload = XhrUpload(),
    onProgress: (Long, Long) -> Unit = { _, _ -> },
): CaptureDto = upload.put("/screenshot-uploads", body, contentType, onProgress = onProgress, uploadTicket = ticket) {
    API_JSON.decodeFromString<CaptureDto>(it)
}

private fun captureBase(slug: String, platform: StorePlatform): String =
    "/projects/${segment(slug)}/screenshots/${segment(platform.id)}"
